import React, { useState, useRef, useEffect } from "react";
import growth1Img from "../../assets/growth1.png";
import growth2Img from "../../assets/growth2.png";
import growth3Img from "../../assets/growth3.png";
import growth4Img from "../../assets/growth4.png";
import certificateImg from "../../assets/certificate2.webp";
import TopInstructors from "../home/TopInstructors";
import RealResults from "../home/RealResults";
import CustomYouTubePlayer from "../learning/CustomYouTubePlayer";
import { getCourseDetails, unlockFreeResource } from "../../api/contentApi";
import { getLearningDashboard } from "../../api/learningApi";
import { createPhonePePayment } from "../../api/ordersApi";
import ApiContentState from "../common/ApiContentState";
import "../../styles/CourseDetails.css";
import "../../styles/TopInstructors.css";
import "../../styles/RealResults.css";


function PinchZoomFrame({ src, alt, caption }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const startDistanceRef = useRef(null);
  const initialScaleRef = useRef(1);
  const startPanRef = useRef({ x: 0, y: 0 });
  const initialPosRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef(0);

  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  

  const getMidpoint = (touches) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      startDistanceRef.current = getDistance(e.touches);
      initialScaleRef.current = scale;

      const mid = getMidpoint(e.touches);
      startPanRef.current = mid;
      initialPosRef.current = { ...position };
    }

    if (e.touches.length === 1 && scale > 1) {
      startPanRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      initialPosRef.current = { ...position };
    }

    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        if (scale > 1) {
          resetZoom();
        } else {
          setScale(2.5);
          setPosition({ x: 0, y: 0 });
        }
      }
      lastTapRef.current = now;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && startDistanceRef.current) {
      if (e.cancelable) e.preventDefault();

      const currentDistance = getDistance(e.touches);
      const ratio = currentDistance / startDistanceRef.current;
      const targetScale = Math.min(Math.max(initialScaleRef.current * ratio, 1), 3.8);
      setScale(targetScale);

      const currentMid = getMidpoint(e.touches);
      const deltaX = currentMid.x - startPanRef.current.x;
      const deltaY = currentMid.y - startPanRef.current.y;

      setPosition({
        x: initialPosRef.current.x + deltaX,
        y: initialPosRef.current.y + deltaY,
      });
    }

    if (e.touches.length === 1 && scale > 1) {
      if (e.cancelable) e.preventDefault();

      const deltaX = e.touches[0].clientX - startPanRef.current.x;
      const deltaY = e.touches[0].clientY - startPanRef.current.y;

      setPosition({
        x: initialPosRef.current.x + deltaX,
        y: initialPosRef.current.y + deltaY,
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      startDistanceRef.current = null;
    }

    if (scale <= 1.05) {
      resetZoom();
    }
  };

  return (
    <div
      className="cd-screenshot-frame"
      style={{
        zIndex: scale > 1 ? 9999 : 2,
        overflow: "visible",
      }}
    >
      <div
        className="cd-screenshot-img-wrap cd-pinch-zoom-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: scale > 1 ? "none" : "pan-y",
          overflow: "visible",
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
            transformOrigin: "center center",
            transition: startDistanceRef.current
              ? "none"
              : "transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1)",
            boxShadow:
              scale > 1
                ? "0 25px 60px rgba(0, 0, 0, 0.9), 0 0 0 1.5px rgba(0, 132, 255, 0.5)"
                : "none",
            position: "relative",
            zIndex: scale > 1 ? 10000 : 1,
            cursor: scale > 1 ? "grab" : "default",
          }}
        />
        {scale > 1 && (
          <span
            className="cd-zoom-reset-badge"
            onClick={(e) => {
              e.stopPropagation();
              resetZoom();
            }}
          >
            ✕ Reset
          </span>
        )}
      </div>
      {caption && <span className="cd-screenshot-caption">{caption}</span>}
    </div>
  );
}

function parseCourseFeatures(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  }

  const text = String(value).trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Store plain text values as delimiter-separated features.
  }

  return text
    .split(/[\r\n,|]+/)
    .map((item) => item.replace(/^[-*•✓✔]+\s*/, "").trim())
    .filter(Boolean);
}

function formatRemainingDuration(minutes) {
  const totalMinutes = Math.ceil(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
}

/* 
   COURSES DATABASE*/
const COURSES_DATABASE = {
  "beyond-school-skills": {
    id: "beyond-school-skills",
    title: "Beyond School. Build Skills.",
    tagline:
      "Learn content creation and video editing from Graduate FF & his team, the minds behind 5+ channels, 2B+ views, and 10M+ subscribers.",
    instructor: "Graduate FF",
    instructorRole: "Mentor • 9M+ Social Subscribers",
    instructorAvatar:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=500&q=80",
    badge: "Flagship Bestseller 🔥",
    rating: "4.9",
    reviewsCount: "2,450+",
    enrolledCount: "50K+ Happy Learners",
    price: 1299,
    originalPrice: "₹3,299",
    duration: "8h 45m",
    modulesCount: 10,
    lecturesCount: 65,
    language: "Hindi",
    certification: "Yes",
    trailerUrl:
      "https://www.youtube.com/embed/-34KeSMV0SI?enablejsapi=1&autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1",
    highlights: [
      "🚀 Create content that people can't ignore & spike retention",
      "🎬 Script, shoot, and edit videos like high-budget productions",
      "🤖 Master the complete AI-powered workflow for 3x output speed",
      "📈 Master YouTube algorithm trajectories and browse feature triggers",
      "💰 Build multiple scalable income sources as a full-time creator",
    ],
  },
  "premiere-pro-mastery": {
    id: "premiere-pro-mastery",
    title: "Video Editing Mastery with Premiere Pro",
    tagline:
      "Master cinematic pacing, sound design, color grading, and viral retention editing techniques used across 2B+ views.",
    instructor: "Graduate FF Editing Team",
    instructorRole: "Lead Video Editors • 8+ Yrs Exp.",
    instructorAvatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
    badge: "High Retention 🎬",
    rating: "4.8",
    reviewsCount: "1,820+",
    enrolledCount: "18K+ Happy Learners",
    price: 999,
    originalPrice: "₹2,499",
    duration: "6h 30m",
    modulesCount: 6,
    lecturesCount: 48,
    language: "Hindi",
    certification: "Yes",
    trailerUrl:
      "https://www.youtube.com/embed/-34KeSMV0SI?enablejsapi=1&autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1",
    highlights: [
      "🎨 Advanced Lumetri Color Grading & Custom Skin Tone Fixes",
      "🔊 Sound Design, Audition Sync, and Noise Removal",
      "⚡ Timeline Speed Ramping and Seamless Transitions",
      "📁 50+ Free Motion Graphics Templates & Presets Included",
      "🎬 Pacing Secrets for 70%+ Audience Retention",
    ],
  },
  "instagram-reels-strategy": {
    id: "instagram-reels-strategy",
    title: "Instagram Reels Strategy: Views to Followers",
    tagline:
      "Crack the Instagram algorithm, engineer viral hooks, leverage trending audio, and turn short video views into a hyper-engaged community.",
    instructor: "Priya Verma",
    instructorRole: "Instagram Growth Strategist • 500K+ Followers",
    instructorAvatar:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=500&q=80",
    badge: "Viral Growth 🚀",
    rating: "4.8",
    reviewsCount: "1,410+",
    enrolledCount: "15K+ Happy Learners",
    price: 899,
    originalPrice: "₹1,999",
    duration: "5h 15m",
    modulesCount: 6,
    lecturesCount: 36,
    language: "Hindi",
    certification: "Yes",
    trailerUrl:
      "https://www.youtube.com/embed/-34KeSMV0SI?enablejsapi=1&autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1",
    highlights: [
      "🧲 Hook scripts that stop scrolling in 3 seconds",
      "🎵 Early trending audio discovery framework",
      "📱 Mobile-first shooting & lighting setups",
      "🔄 Converting viral reel views into long-term followers",
      "📊 Analyzing Reels Insights for strategic pivots",
    ],
  },
  "sponsorships-blueprint": {
    id: "sponsorships-blueprint",
    title: "How to Land $1,000+ Brand Sponsorships",
    tagline:
      "Learn the exact cold outreach emails, media kit designs, rate calculation formulas, and deal negotiation scripts to start earning brand deals.",
    instructor: "Tanmay Bhasin",
    instructorRole: "Creator Manager & Sponsorship Lead",
    instructorAvatar:
      "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=500&q=80",
    badge: "High ROI 💰",
    rating: "4.9",
    reviewsCount: "920+",
    enrolledCount: "8K+ Happy Learners",
    price: 1199,
    originalPrice: "₹2,999",
    duration: "4h 45m",
    modulesCount: 5,
    lecturesCount: 30,
    language: "Hindi",
    certification: "Yes",
    trailerUrl:
      "https://www.youtube.com/embed/-34KeSMV0SI?enablejsapi=1&autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1",
    highlights: [
      "✉️ Cold Pitch Email Templates that brands actually open",
      "📊 Canva Media Kit layout files ready to customize",
      "💵 Rate card calculation formula per view count",
      "📜 Sponsorship contract safeguards & payment protection",
      "🤝 Building long-term brand ambassador retainer deals",
    ],
  },
  "ai-content-workflow": {
    id: "ai-content-workflow",
    title: "AI Tools for Creators: Supercharge Production",
    tagline:
      "Integrate generative AI into scripting, thumbnail ideation, automated captions, B-roll generation, and voiceovers to cut editing time in half.",
    instructor: "Kabir Roy",
    instructorRole: "AI Content Researcher & Creator",
    instructorAvatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80",
    badge: "Next Gen 🤖",
    rating: "4.7",
    reviewsCount: "640+",
    enrolledCount: "6K+ Happy Learners",
    price: 799,
    originalPrice: "₹1,999",
    duration: "4h 10m",
    modulesCount: 5,
    lecturesCount: 28,
    language: "Hindi",
    certification: "Yes",
    trailerUrl:
      "https://www.youtube.com/embed/-34KeSMV0SI?enablejsapi=1&autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1",
    highlights: [
      "🤖 Master Midjourney & DALL-E for thumbnail art",
      "✍️ Script writing prompts for ChatGPT & Claude",
      "🗣️ AI Voice cloning and automated caption sync",
      "⏩ Cut post-production editing time by 50%+",
      "🛠️ Top 10 free AI tools every creator must use",
    ],
  },
};



export default function CourseDetails({
  courseId = "beyond-school-skills",
  courseByTitle = false,
  currentUser,
}) {
  const [databaseCourse, setDatabaseCourse] = useState(null);
  const [isLoadingCourse, setIsLoadingCourse] = useState(true);
  const [courseLoadError, setCourseLoadError] = useState("");
  const [isExecutiveVideoPlaying, setIsExecutiveVideoPlaying] = useState(false);
    const executiveVideoRef = useRef(null);
    const executiveIframeRef = useRef(null);
  
  const [courseAccess, setCourseAccess] = useState({
    status: localStorage.getItem("cd_user_token") ? "checking" : "available",
    remainingMinutes: 0,
  });

  
  const handleToggleExecutiveVideo = () => {
    if (!isExecutiveVideoPlaying) {
      setIsExecutiveVideoPlaying(true);
      if (executiveIframeRef.current && executiveIframeRef.current.contentWindow) {
        executiveIframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: [] }),
          "*"
        );
      }
      if (executiveVideoRef.current) {
        executiveVideoRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      setIsExecutiveVideoPlaying(false);
      if (executiveIframeRef.current && executiveIframeRef.current.contentWindow) {
        executiveIframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*"
        );
      }
    }
  };

  const [accessCheckVersion, setAccessCheckVersion] = useState(0);
  const courseData = databaseCourse;
  const bonusItems = Array.isArray(courseData?.bonuses)
    ? courseData.bonuses
    : Array.isArray(courseData?.bonuses?.items)
      ? courseData.bonuses.items
      : [];
  const bonusValue = courseData?.bonusValue || courseData?.bonuses?.subtitle || "";
  const [expandedModules, setExpandedModules] = useState({ 1: true });
  const [openFaq, setOpenFaq] = useState(null);
  const [activeBonusModal, setActiveBonusModal] = useState(null);
  const [isBonusUnlocked, setIsBonusUnlocked] = useState(false);
  const [bonusLeadForm, setBonusLeadForm] = useState({ name: "", email: "", phone: "" });
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState("idle");
  const [purchaseError, setPurchaseError] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "" });
    
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 14,
    minutes: 36,
    seconds: 28,
  });

  const handleOpenBonusModal = async (bonus) => {
    const token = localStorage.getItem("cd_user_token");

    // Login nahi hai
    if (!token) {
      window.location.hash = "#login";
      return;
    }

    try {
      await unlockFreeResource(bonus.id);

      const downloadUrls = [...new Set(
        bonusItems
          .map((item) => item.downloadUrl || item.link)
          .filter((url) => typeof url === "string" && url.trim()),
      )];

      if (!downloadUrls.length) {
        alert("Download links are not available for these resources yet.");
        return;
      }

      downloadUrls.forEach((url, index) => {
        window.setTimeout(() => {
          const link = document.createElement("a");
          link.href = url;
          link.download = "";
          document.body.appendChild(link);
          link.click();
          link.remove();
        }, index * 300);
      });
    } catch (error) {
      if (error.status === 401) {
        localStorage.removeItem("cd_user_token");
        localStorage.removeItem("cd_auth_user");
        window.location.hash = "#login";
        return;
      }

      alert(error.message || "Unable to download resource.");
    }
  };

  const handleCloseBonusModal = () => {
    setActiveBonusModal(null);
    setIsBonusUnlocked(false);
  };

  const handleBonusFormSubmit = (event) => {
    event.preventDefault();
    setIsBonusUnlocked(true);
  };

  useEffect(() => {
    const [, queryString = ""] = window.location.hash.split("?");
    const params = new URLSearchParams(queryString);
    const paymentResult = params.get("payment");
    const paymentStatus = params.get("status") || paymentResult;

    if (
      paymentResult === "details" &&
      paymentStatus === "success"
    ) {
      setPurchaseStatus("success");
      setPurchaseDialogOpen(true);
    } else if (
      ["failed", "pending", "verification-error", "invalid"].includes(
        paymentStatus,
      )
    ) {
      setPurchaseStatus("error");
      setPurchaseError(
        paymentStatus === "failed"
          ? "PhonePe reports that this payment was not completed."
          : paymentStatus === "pending"
            ? "Payment is still pending. Please check your orders shortly."
            : "We could not verify this payment yet. Please check your orders or contact support.",
      );
      setPurchaseDialogOpen(true);
    } else if (paymentResult === "success") {
      setPurchaseStatus("success");
      setPurchaseDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((previous) => {
        if (previous.seconds > 0) {
          return { ...previous, seconds: previous.seconds - 1 };
        }
        if (previous.minutes > 0) {
          return { ...previous, minutes: previous.minutes - 1, seconds: 59 };
        }
        if (previous.hours > 0) {
          return { ...previous, hours: previous.hours - 1, minutes: 59, seconds: 59 };
        }
        if (previous.days > 0) {
          return { ...previous, days: previous.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return previous;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const lookup = String(courseId || '').trim();
    if (!lookup) {
      setDatabaseCourse(null);
      setIsLoadingCourse(false);
      setCourseLoadError("Course not found.");
      return undefined;
    }

    let cancelled = false;
    setIsLoadingCourse(true);
    setCourseLoadError("");
    setDatabaseCourse(null);

    getCourseDetails(lookup, courseByTitle)
      .then((response) => {
        if (cancelled) return;

        if (!response?.status || !response.course) {
          setDatabaseCourse(null);
          setCourseLoadError("Course not found.");
          return;
        }

        const course = response.course;
        const featureText = String(course.features || '');
        const reviews = Array.isArray(course.reviews) ? course.reviews : [];
        const averageRating = reviews.length
          ? reviews.reduce((total, review) => total + Number(review.rating || 0), 0) / reviews.length
          : null;

        setDatabaseCourse({
          id: String(course.id),
          title: course.title,
          tagline: course.about || course.details || '',
          instructor: course.instructor,
          instructorRole: course.instructorRole,
          instructorAvatar: course.instructorAvatar,
          badge: course.courseType,
          rating: course.rating ?? averageRating,
          reviewsCount: String(reviews.length),
          enrolledCount: course.enrolledCount,
          price: Number(course.totalPrice ?? course.price ?? 0),
          originalPrice: course.price ? `₹${course.price}` : '',
          duration: course.duration ? `${course.duration}h` : '',
          modulesCount: (course.modules || []).length,
          lecturesCount: (course.modules || []).reduce(
            (total, module) => total + (module.lectures || []).length,
            0,
          ),
          language: course.language,
          certification: course.certification,
          trailerUrl: course.videoUrl,
          includedFeatures: course.includedFeatures || course.features || '',
          features: course.features || '',
          videos: course.videos || [],
          projects: course.projects || [],
          dashboard: course.dashboard || null,
          highlights: featureText
            .split(/[\n.!?]/)
            .map((item) => item.trim())
            .filter(Boolean)
            .slice(0, 5),
          bonuses: course.bonuses?.items || [],
          bonusValue: course.bonuses?.subtitle,
          databaseModules: course.modules || [],
          databaseFaqs: course.faqs || [],
          databaseReviews: reviews,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setDatabaseCourse(null);
          setCourseLoadError(error.message || "Unable to load course details.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCourse(false);
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, courseByTitle]);

  useEffect(() => {
    if (!localStorage.getItem("cd_user_token")) {
      setCourseAccess({ status: "available", remainingMinutes: 0 });
      return undefined;
    }

    let cancelled = false;
    setCourseAccess({ status: "checking", remainingMinutes: 0 });

    getLearningDashboard()
      .then((response) => {
        if (cancelled) return;

        const resolvedCourseId = databaseCourse?.id;
        const activeCourse = resolvedCourseId
          ? (response?.courses || []).find(
              (course) => String(course.id) === String(resolvedCourseId),
            )
          : null;
        if (!activeCourse) {
          setCourseAccess({ status: "available", remainingMinutes: 0 });
          return;
        }

        const remainingMinutes = (activeCourse.lectures || []).reduce(
          (total, lecture) => total + (
            lecture.isCompleted ? 0 : Math.max(0, Number(lecture.duration) || 0)
          ),
          0,
        );
        setCourseAccess({ status: "enrolled", remainingMinutes });
      })
      .catch((error) => {
        console.error("Unable to verify course enrollment:", error);
        if (!cancelled) {
          if (error.status === 401) {
            localStorage.removeItem("cd_user_token");
            localStorage.removeItem("cd_auth_user");
            setCourseAccess({ status: "available", remainingMinutes: 0 });
          } else {
            setCourseAccess({ status: "error", remainingMinutes: 0 });
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, currentUser, accessCheckVersion, databaseCourse?.id]);

  const courseCurriculum = (databaseCourse?.databaseModules || []).map((module) => ({
        id: module.id,
        title: module.title,
        meta: `${module.lectureCount || module.lectures?.length || 0} Lectures`,
        lectures: (module.lectures || []).map((lecture) => ({
          title: lecture.title,
          time: lecture.duration ? `${lecture.duration}m` : '',
        })),
      }));
  const faqsData = (databaseCourse?.databaseFaqs || [])
    .filter((faq) => faq.question && faq.answer)
    .map((faq) => ({ q: faq.question, a: faq.answer }));

  const toggleModule = (id) => {
    setExpandedModules((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFaq = (idx) => {
    setOpenFaq((prev) => (prev === idx ? null : idx));
  };

  const handleEnrollClick = async () => {
    if (isPurchasing) return;

    if (!localStorage.getItem("cd_user_token")) {
      setPurchaseDialogOpen(false);
      window.location.hash = "#login";
      return;
    }
    if (courseAccess.status !== "available") return;

    setPurchaseDialogOpen(true);
    setPurchaseError("");
    setIsPurchasing(true);
    setPurchaseStatus("processing");
    setPurchaseError("");

    try {
      const response = await createPhonePePayment(courseData.id);
      window.location.assign(response.paymentUrl);
    } catch (error) {
      if (error.status === 401) {
        localStorage.removeItem("cd_user_token");
        localStorage.removeItem("cd_auth_user");
        setPurchaseDialogOpen(false);
        window.location.hash = "#login";
        return;
      }

      setPurchaseError(error.message || "Unable to purchase this course.");
      setPurchaseStatus("error");
    } finally {
      setIsPurchasing(false);
    }
  };

  const includedCourseFeatures = parseCourseFeatures(
    courseData?.includedFeatures || courseData?.features,
  );

  const renderEnrollmentAction = (className, label) => {
    if (!localStorage.getItem("cd_user_token")) {
      return (
        <button className={className} onClick={handleEnrollClick}>
          {label}
        </button>
      );
    }

    if (courseAccess.status === "checking") {
      return (
        <div className="cd-access-checking" role="status" aria-label="Checking course access">
          <span className="api-content-loader" aria-hidden="true" />
        </div>
      );
    }

    if (courseAccess.status === "enrolled") {
      return (
        <div className="cd-course-access-state" role="status">
          <strong>✓ Purchased · Lifetime access</strong>
          <span>
            {courseAccess.remainingMinutes > 0
              ? `Remaining course duration: ${formatRemainingDuration(courseAccess.remainingMinutes)}`
              : "Course completed"}
          </span>
        </div>
      );
    }

    if (courseAccess.status === "error") {
      return (
        <div className="cd-course-access-state" role="alert">
          <span>Unable to verify course access.</span>
          <button
            className="cd-access-retry-btn"
            onClick={() => setAccessCheckVersion((version) => version + 1)}
          >
            Retry
          </button>
          <button className={className} onClick={handleEnrollClick}>
            {label}
          </button>
        </div>
      );
    }

    return (
      <button className={className} onClick={handleEnrollClick}>
        {label}
      </button>
    );
  };

  if (!courseData) {
    if (purchaseDialogOpen) {
      const paymentSucceeded = purchaseStatus === "success";

      return (
        <div className="cd-course-details-page">
          <div className="cd-purchase-dialog-curtain">
            <div className="cd-purchase-dialog-backdrop" />
            <div
              className="cd-purchase-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cd-purchase-dialog-title"
            >
              <button
                className="cd-purchase-dialog-close"
                type="button"
                onClick={() => setPurchaseDialogOpen(false)}
                aria-label="Close payment result"
              >
                ✕
              </button>
              <div className="cd-purchase-dialog-state">
                <span
                  className={`cd-purchase-dialog-icon${paymentSucceeded ? " success" : " error"}`}
                >
                  {paymentSucceeded ? "✓" : "!"}
                </span>
                <h3 id="cd-purchase-dialog-title">
                  {paymentSucceeded ? "Payment Successful" : "Payment Update"}
                </h3>
                <p>
                  {paymentSucceeded
                    ? "PhonePe confirmed your payment. Your course order is being finalized."
                    : purchaseError ||
                      "We could not verify this payment yet. Please check your orders shortly."}
                </p>
                <button
                  className="cd-purchase-dialog-primary"
                  type="button"
                  onClick={() => setPurchaseDialogOpen(false)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="cd-course-details-page">
        <ApiContentState
          loading={isLoadingCourse}
          error={courseLoadError}
          emptyMessage="Course details are not available."
        />
      </div>
    );
  }

  return (
    <div className="cd-course-details-page">
      {/* BREADCRUMB NAVIGATION */}
      <div className="cd-details-breadcrumb">
        <div className="cd-details-max-width">
          <span
            className="cd-breadcrumb-link"
            onClick={() => (window.location.hash = "#home")}
          >
            Home
          </span>
          <span className="cd-breadcrumb-arrow">➔</span>
          <span
            className="cd-breadcrumb-link"
            onClick={() => (window.location.hash = "#courses")}
          >
            Courses
          </span>
          <span className="cd-breadcrumb-arrow">➔</span>
          <span className="cd-breadcrumb-current">{courseData.title}</span>
        </div>
      </div>

      <div className="cd-details-max-width cd-details-main-grid">
        {/* LEFT COLUMN PANEL */}
        <div className="cd-details-left-content">
          {/* HERO BANNER CARD */}
          <div className="cd-hero-banner-card">
            <div className="cd-hero-top-split">
              <div className="cd-hero-left-info">
                <span className="cd-badge-bestseller">
                  <span className="cd-badge-dot">●</span> {courseData.badge}
                </span>

                <h1 className="cd-course-main-title">{courseData.title}</h1>
                <p className="cd-course-tagline">{courseData.tagline}</p>

                <div className="cd-hero-meta-row">
                  <span className="cd-meta-rating">
                    ★ {courseData.rating}{" "}
                    <span className="cd-meta-stars-gold">★★★★★</span>{" "}
                    <span className="cd-meta-count">
                      ({courseData.reviewsCount} Reviews)
                    </span>
                  </span>
                  <span className="cd-meta-divider">|</span>
                  <span className="cd-meta-enrolled">{courseData.enrolledCount}</span>
                </div>

                <div className="cd-instructor-mini-profile">
                  <img
                    src={courseData.instructorAvatar}
                    alt={courseData.instructor}
                  />
                  <div className="cd-instructor-text">
                    <h4>{courseData.instructor}</h4>
                    <p>{courseData.instructorRole}</p>
                  </div>
                </div>
              </div>

              {/* RIGHT MEDIA VIDEO FRAME */}
              <div className="cd-hero-right-media">
                <div className="cd-video-preview-thumbnail-box">
                  <CustomYouTubePlayer
                    videoUrl={courseData.trailerUrl}
                    title={courseData.title}
                    className="course-card-player"
                    autoPlayWhenVisible
                    mutedAutoplay
                  />
                  {/* <span className="cd-preview-runtime-badge">
                    ⏱️ {courseData.duration} Masterclass
                  </span> */}
                </div>
              </div>
            </div>

            {/* LOWER KEY METRICS RIBBON */}
            <div className="cd-hero-key-metrics-ribbon">
              <div className="cd-metric-item">
                ⏱️{" "}
                <div>
                  <p>Duration</p>
                  <h4>{courseData.duration}</h4>
                </div>
              </div>
              <div className="cd-metric-item">
                📦{" "}
                <div>
                  <p>Modules</p>
                  <h4>{courseData.modulesCount} Modules</h4>
                </div>
              </div>
              <div className="cd-metric-item">
                🎥{" "}
                <div>
                  <p>Lectures</p>
                  <h4>{courseData.lecturesCount} Lessons</h4>
                </div>
              </div>
              <div className="cd-metric-item">
                🗣️{" "}
                <div>
                  <p>Language</p>
                  <h4>{courseData.language}</h4>
                </div>
              </div>
              <div className="cd-metric-item">
                📜{" "}
                <div>
                  <p>Certification</p>
                  <h4>{courseData.certification}</h4>
                </div>
              </div>
            </div>
          </div>

          {/* 1. MEET YOUR MENTOR (REUSABLE TopInstructors COMPONENT) */}
          <div className="cd-section-wrapper-block cd-mentor-override-block">
            <TopInstructors
              mentor={{
                name: courseData.instructor,
                title: courseData.instructorRole,
                avatar: courseData.instructorAvatar,
                experience: "8+ Years Exp.",
                subscribers: "9M+ Followers",
                views: "2B+ Total Views",
                channels: "5+ Active Channels",
                achievements: "Most Subscribed YouTube Channel in 2022 & 2023 Globally",
                skillsTaught: [
                  "YouTube Growth",
                  "Video Editing",
                  "Content Creation",
                  "AI Workflows",
                  "Monetization",
                ],
              }}
              showCta={false}
            />
          </div>

          {/* 2. TWO SCREENSHOT DISPLAYS (INSIDE LEARNING DASHBOARD) */}
          <div className="cd-section-wrapper-block">
            <h3 className="cd-section-accent-header">
              Inside The Learning Dashboard
            </h3>
            <p className="cd-sub-intro-text">
              High-bitrate video lessons, editable project timelines, and practical
              downloadable presets.
            </p>
            <div className="cd-screenshots-dual-grid">
              <PinchZoomFrame
                src={growth3Img}
                alt="Dashboard View 1"
                caption="Timeline Breakdown & Sound Design Modules"
              />
              <PinchZoomFrame
                src={growth4Img}
                alt="Dashboard View 2"
                caption="Resource Downloads, LUT Packs & Presets"
              />
            </div>
          </div>

          {/* 3. FREE BONUSES */}

           {bonusItems && bonusItems.length > 0 && (
            <div className="cd-bonus-section-wrapper">
              <div className="cd-bonus-header">
                <h3 className="cd-bonus-title">
                  Unlock Free Resources Worth{" "}
                  <span className="cd-bonus-highlight">
                    {bonusValue}
                  </span>
                </h3>
                <p className="cd-bonus-subtitle">
                  Register for the Masterclass and get all exclusive resources,
                  absolutely for FREE!
                </p>
              </div>

              <div className="cd-bonus-grid">
                {bonusItems.map((bonus) => (
                  <div key={bonus.id} className="cd-bonus-card">
                    <div className="cd-book-3d-stage">
                      <div
                        className="cd-bonus-book-mockup"
                        style={{
                          backgroundColor: bonus.themeColor || bonus.color || "#0f172a",
                          boxShadow: `0 14px 28px -6px ${(bonus.themeColor || bonus.color || "#0f172a")}55`,
                        }}
                      >
                        <span className="cd-book-page cd-page-under" />
                        <span className="cd-book-page cd-page-middle" />
                        <span className="cd-book-page cd-page-top" />

                        <div
                          className="cd-book-front-cover"
                          style={{ backgroundColor: bonus.themeColor || bonus.color || "#0f172a" }}
                        >
                          <div className="cd-book-spine" />
                          <div className="cd-book-icon-wrap">{bonus.icon || "📦"}</div>
                          <span className="cd-book-subtext">{bonus.label || bonus.title}</span>
                        </div>
                      </div>
                    </div>

                    <h4 className="cd-bonus-name">{bonus.title}</h4>

                    <div className="cd-bonus-pill">
                      <span className="cd-bonus-old-price">
                        {bonus.originalPrice || bonus.originalPriceText || "₹0"}
                      </span>
                      <span className="cd-bonus-free-txt">FREE</span>
                    </div>

                    <button
                      type="button"
                      className="cd-bonus-unlock-btn"
                      onClick={() => handleOpenBonusModal(bonus)}
                    >
                      🔒 Unlock Now
                    </button>
                  </div>
                ))}
              </div>

              {activeBonusModal && (
                <div className="cd-bonus-modal-curtain">
                  <div
                    className="cd-bonus-modal-backdrop"
                    onClick={handleCloseBonusModal}
                  />
                  <div className="cd-bonus-modal-card">
                    <button
                      className="cd-bonus-modal-close"
                      onClick={handleCloseBonusModal}
                    >
                      ✕
                    </button>

                    {!isUnlocked ? (
                      <div className="cd-bonus-form-content">
                        <div className="cd-bonus-modal-icon-header">
                          <div
                            className="cd-bonus-modal-badge-icon"
                            style={{
                              backgroundColor: activeBonusModal.themeColor || activeBonusModal.color || "#0f172a",
                            }}
                          >
                            {activeBonusModal.icon}
                          </div>
                          <h3>Unlock Free Resource</h3>
                          <p>
                            Enter your details to get instant download access to{" "}
                            <strong>{activeBonusModal.title}</strong> (
                            {activeBonusModal.originalPrice} Value).
                          </p>
                        </div>

                        <form
                          onSubmit={handleBonusFormSubmit}
                          className="cd-bonus-lead-form"
                        >
                          <div className="cd-form-group">
                            <label>Full Name</label>
                            <input
                              type="text"
                              required
                              placeholder="Enter your name"
                              value={leadForm.name}
                              onChange={(e) =>
                                setLeadForm({
                                  ...leadForm,
                                  name: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="cd-form-group">
                            <label>Email Address</label>
                            <input
                              type="email"
                              required
                              placeholder="name@example.com"
                              value={leadForm.email}
                              onChange={(e) =>
                                setLeadForm({
                                  ...leadForm,
                                  email: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="cd-form-group">
                            <label>WhatsApp / Phone Number</label>
                            <input
                              type="tel"
                              required
                              placeholder="+91 98765 43210"
                              value={leadForm.phone}
                              onChange={(e) =>
                                setLeadForm({
                                  ...leadForm,
                                  phone: e.target.value,
                                })
                              }
                            />
                          </div>

                          <button type="submit" className="cd-bonus-submit-btn">
                            🔓 Submit & Unlock Resource
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="cd-bonus-success-content">
                        <div className="cd-success-icon-bubble">🎉</div>
                        <h3>Resource Unlocked!</h3>
                        <p>
                          Hey <strong>{leadForm.name}</strong>, your access link
                          for <strong>{activeBonusModal.title}</strong> is
                          ready.
                        </p>

                        <a
                          href="#download"
                          className="cd-bonus-download-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            alert(
                              `Downloading ${activeBonusModal.title}...`
                            );
                          }}
                        >
                          📥 Download Resource Now
                        </a>

                        <span className="cd-success-footnote">
                          A backup link has also been sent to{" "}
                          <strong>{leadForm.email}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
      {/* <section className="ve-max-width ve-flex-bonus-section">
        <div className="ve-flex-bonus-header">
          <span className="ve-flex-kicker">FREE BONUSES INCLUDED</span>
          <h3 className="ve-flex-bonus-title">
            Unlock Free Resources Worth{" "}
            <span className="ve-text-gradient">{bonusValue}</span>
          </h3>
          <p className="ve-flex-bonus-subtitle">
            Get these professional asset packs included with your course
            enrollment.
          </p>
        </div>

        <div className="ve-flex-bonus-row">
          
          {editingBonuses.map((bonus) => (
            <div key={bonus.id} className="ve-flex-bonus-card">
              <div className="ve-book-3d-stage">
                <div
                  className="ve-bonus-book-mockup"
                  style={{
                    backgroundColor: bonus.themeColor,
                    boxShadow: `0 14px 28px -6px ${bonus.themeColor}55`,
                  }}
                >
                  <span className="ve-book-page ve-page-under" />
                  <span className="ve-book-page ve-page-middle" />
                  <span className="ve-book-page ve-page-top" />
                  <div
                    className="ve-book-front-cover"
                    style={{ backgroundColor: bonus.themeColor }}
                  >
                    <div className="ve-book-spine" />
                    <div className="ve-book-icon-wrap">{bonus.icon}</div>
                    <span className="ve-book-subtext">{bonus.label}</span>
                  </div>
                </div>
              </div>

              <div className="ve-flex-card-body">
                <h4 className="ve-flex-card-title">{bonus.title}</h4>
                <div className="ve-flex-price-pill">
                  <del className="ve-flex-old-price">{bonus.originalPrice}</del>
                  <span className="ve-flex-free-tag">100% FREE</span>
                </div>
              </div>

              {courseAccess.status === "enrolled" ? (
                <span className="ve-flex-unlock-included">Included with your course</span>
              ) : (
                <button
                  type="button"
                  className="ve-flex-unlock-btn"
                  onClick={handleEnrollClick}
                >
                  Enroll to unlock
                </button>
              )}
            </div>
          ))}
        </div>
      </section> */}


          {/* 4. DESCRIPTION & HIGHLIGHTS */}
          <div className="cd-section-wrapper-block">
            <h3 className="cd-section-accent-header">
              Course Overview & Highlights
            </h3>
            <p className="cd-long-description-paragraph">{courseData.tagline}</p>
            <ul className="cd-check-benefits-bullet-list">
              {courseData.highlights.map((point, idx) => (
                <li key={idx}>✓ {point}</li>
              ))}
            </ul>
          </div>

          {/* 5. DETAILED COURSE CONTENT */}
          <div className="cd-section-wrapper-block">
            <div className="cd-content-section-header-flex">
              <h3 className="cd-section-accent-header">Detailed Course Content</h3>
              <span>
                {courseData.modulesCount} Modules • {courseData.lecturesCount}{" "}
                Lectures • {courseData.duration}
              </span>
            </div>

            <div className="cd-accordion-chassis-stack">
              {courseCurriculum.length === 0 ? (
                <ApiContentState
                  loading={false}
                  emptyMessage="No curriculum is available for this course."
                />
              ) : courseCurriculum.map((module) => (
                <div key={module.id} className="cd-accordion-node-card">
                  <div
                    className="cd-accordion-trigger-row"
                    onClick={() => toggleModule(module.id)}
                  >
                    <div className="cd-trigger-left">
                      <span className="cd-arrow-indicator-icon">
                        {expandedModules[module.id] ? "▼" : "▶"}
                      </span>
                      <h4>{module.title}</h4>
                    </div>
                    <span className="cd-trigger-meta-lbl">{module.meta}</span>
                  </div>

                  {expandedModules[module.id] && (
                    <div className="cd-accordion-internal-dropdown-drawer">
                      {module.lectures.map((lecture, i) => (
                        <div key={i} className="cd-lecture-item-line">
                          <div className="cd-lecture-left-txt">
                            🎥 <p>{lecture.title}</p>
                          </div>
                          <span className="cd-lecture-duration-timestamp">
                            {lecture.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 6. GROWTH WHICH YOU CAN ALSO HAVE */}
          <div className="cd-section-wrapper-block">
            <div className="cd-growth-header-row">
              <div>
                <span className="cd-growth-kicker">PROVEN PRODUCER RESULTS</span>
                <h3 className="cd-section-accent-header">
                  Growth which you can also have
                </h3>
                <p className="cd-sub-intro-text">
                  Real retention spikes, view velocity, and audience watch-time
                  analytics achieved by applying our editing blueprints.
                </p>
              </div>
              <div className="cd-growth-live-pill">
                <span className="cd-pulse-dot" /> YouTube Studio Analytics
              </div>
            </div>

            <div className="cd-screenshots-dual-grid">
              <PinchZoomFrame src={growth1Img} alt="Growth Screenshot 1" />
              <PinchZoomFrame src={growth2Img} alt="Growth Screenshot 2" />
            </div>
          </div>

              <div className="cd-section-wrapper-block ve-cert-box">
                      <div className="ve-cert-left">
                        <span className="ve-kicker">OFFICIAL CREDENTIAL</span>
                        <h3
                          className="cd-section-accent-header"
                          style={{ marginBottom: "12px" }}
                        >
                          Get Industry-Recognized Certification
                        </h3>
                        <p
                          className="cd-sub-intro-text"
                          style={{ marginBottom: "18px" }}
                        >
                          Showcase your accredited skills to creators, agencies, and
                          international clients on LinkedIn & Upwork.
                        </p>
                        <ul className="ve-cert-bullet-points">
                          <li>✓ Verifiable digital certificate ID</li>
                          <li>✓ Shareable on LinkedIn & portfolio sites</li>
                          <li>✓ Awarded after completing the final challenge project</li>
                        </ul>
                      </div>
                      <div className="ve-cert-right">
                        <div className="ve-cert-png-wrapper">
                          <img
                            src={certificateImg}
                            alt="Official Course Completion Certificate"
                            className="ve-cert-custom-png"
                            loading="lazy"
                          />
                          <div className="ve-cert-floating-badge">
                            ★ Verified Credential ★
                          </div>
                        </div>
                      </div>
                    </div>

          {/* 7. WHY CHOOSE US SECTION */}
          <div className="cd-section-wrapper-block">
            <h3 className="cd-section-accent-header">Why Choose Creators Dost</h3>
            <div className="cd-why-us-grid">
              <div className="cd-why-card">
                <span className="cd-why-icon">⚡</span>
                <h4>Battle-Tested Workflows</h4>
                <p>
                  Learn exact frameworks used on channels generating millions of
                  monthly views.
                </p>
              </div>
              <div className="cd-why-card">
                <span className="cd-why-icon">🤝</span>
                <h4>Live Community Audits</h4>
                <p>
                  Get your video hooks and editing timelines reviewed directly by
                  veteran editors.
                </p>
              </div>
              <div className="cd-why-card">
                <span className="cd-why-icon">📜</span>
                <h4>Verifiable Credentials</h4>
                <p>
                  Earn industry-recognized certification to showcase to agencies
                  and high-ticket clients.
                </p>
              </div>
              <div className="cd-why-card">
                <span className="cd-why-icon">♾️</span>
                <h4>Lifetime Access & Updates</h4>
                <p>
                  One-time enrollment gives free access to all future modules and
                  masterclass additions.
                </p>
              </div>
            </div>
          </div>

          {/* 8.REAL RESULTS SECTION */}
          <div className="cd-section-wrapper-block cd-reviews-override-block">
            <RealResults />
          </div>

          {/* 9. COURSE FAQS */}
          <div className="cd-section-wrapper-block">
            <h3 className="cd-section-accent-header">Course FAQs</h3>
            {faqsData.length === 0 ? (
              <ApiContentState
                loading={false}
                emptyMessage="No FAQs are available for this course."
              />
            ) : (
              <div className="cd-faqs-flex-dual-boxes">
                {[faqsData.filter((_, index) => index % 2 === 0), faqsData.filter((_, index) => index % 2 === 1)].map(
                  (columnFaqs, columnIndex) => (
                    <div className="cd-faq-column-box" key={columnIndex}>
                      {columnFaqs.map((faq) => {
                        const faqIndex = faqsData.indexOf(faq);
                        return (
                          <div className="cd-faq-mini-item" key={faqIndex}>
                            <div
                              className="cd-faq-question-head"
                              onClick={() => toggleFaq(faqIndex)}
                            >
                              <h4>{faq.q}</h4>
                              <span>{openFaq === faqIndex ? "−" : "+"}</span>
                            </div>
                            {openFaq === faqIndex && (
                              <p className="cd-faq-answer-txt">{faq.a}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          {/* 10. ARE YOU READY SECTION */}
         <div ref={executiveVideoRef} className="cd-section-wrapper-block cd-ready-cta-card">
                     <div className="cd-ready-split-layout">
                       <div className="cd-ready-video-col">
                         <div className="cd-ready-video-wrapper">
                           {isExecutiveVideoPlaying ? (
                             <iframe
                               ref={executiveIframeRef}
                               width="100%"
                               height="100%"
                               src={`${courseData.trailerUrl}&autoplay=1`}
                               title="Course Breakdown Video"
                               frameBorder="0"
                               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                               allowFullScreen
                             />
                           ) : (
                             <div
                               className="cd-ready-video-poster"
                               onClick={handleToggleExecutiveVideo}
                               style={{
                                 backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.7)), url(${growth3Img})`,
                               }}
                             >
                               <div className="cd-executive-play-ring">
                                 <span className="cd-executive-play-icon">▶</span>
                               </div>
                               <div className="cd-poster-caption">
                                 <span>Watch Lecture 1 for Free
         (Pehle experience karein, phir trust karein)</span>
                               </div>
                             </div>
                           )}
                         </div>
                       </div>
         
                       <div className="cd-ready-content-col">
                         <span className="cd-ready-badge">INSIDE THE CURRICULUM</span>
                         <h2 className="cd-ready-heading">Still Confused? Let Our Team Guide You.</h2>
                         <p className="cd-ready-subtext">
                           Watch our executive break down everything inside this masterclass—from timeline project files to monetization pipelines. Learn how this course accelerates your creator career.
                         </p>
         
                         <div className="cd-ready-cta-actions">
                           <button
                             type="button"
                             className="cd-btn-talk-executive"
                             onClick={handleToggleExecutiveVideo}
                           >
                             {isExecutiveVideoPlaying ? "⏸ Pause Video" : "💬 Talk to our Executive ➔"}
                           </button>
                         </div>
         
                         <div className="cd-ready-guarantees-row">
                           <span>⏱️ 5-Min Walkthrough</span>
                           <span>•</span>
                           <span>🎬 Full Curriculum Breakdown</span>
                           <span>•</span>
                           <span>🎯 Roadmap Preview</span>
                         </div>
                       </div>
                     </div>
                   </div>
        </div>

        {/* RIGHT SIDEBAR PANEL */}
        <div className="cd-details-right-sidebar-panel">
          <div className="cd-sidebar-sticky-scrolling-container">
            <div className="cd-checkout-sticky-card">
              <div className="cd-ribbon-discount-tag">
                🔥 60% OFF Limited Time Offer!
              </div>

              <div className="cd-checkout-timer-countdown-row">
                <div className="cd-timer-block">
                  <h4>{String(timeLeft.days).padStart(2, "0")}</h4>
                  <p>Days</p>
                </div>
                <div className="cd-timer-block">
                  <h4>{String(timeLeft.hours).padStart(2, "0")}</h4>
                  <p>Hrs</p>
                </div>
                <div className="cd-timer-block">
                  <h4>{String(timeLeft.minutes).padStart(2, "0")}</h4>
                  <p>Mins</p>
                </div>
                <div className="cd-timer-block">
                  <h4>{String(timeLeft.seconds).padStart(2, "0")}</h4>
                  <p>Secs</p>
                </div>
              </div>

              <div className="cd-checkout-pricing-matrix-row">
                <div>
                  <span className="cd-lbl-offer-price-txt">
                    Special Offer Price
                  </span>
                  <h2 className="cd-main-price-currency-tag">
                    ₹{courseData.price.toLocaleString("en-IN")}
                  </h2>
                </div>
                <div className="cd-original-price-strike-block">
                  <p>Regular Price</p>
                  <span>{courseData.originalPrice}</span>
                </div>
              </div>

              {renderEnrollmentAction(
                "cd-btn-primary-checkout-buy-now",
                "Join Now",
              )}

              <ul className="cd-checkout-bullet-guarantees-list">
                <li>📱 Access on Mobile, Tablet & Desktop</li>
                <li>♾️ Lifetime Access & Future Updates</li>
                <li>🎓 Verified Certificate of Completion</li>
              </ul>
            </div>

            <div className="cd-checkout-sticky-card cd-includes-box">
              <h3>Included With This Course</h3>
              {includedCourseFeatures.length > 0 && (
                <ul className="cd-includes-list">
                  {includedCourseFeatures.map((feature, index) => (
                    <li key={`${feature}-${index}`}>
                      ✓ <strong>{feature}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {purchaseDialogOpen && (
        <div className="cd-purchase-dialog-curtain">
          <div
            className="cd-purchase-dialog-backdrop"
            onClick={() => !isPurchasing && setPurchaseDialogOpen(false)}
          />
          <div className="cd-purchase-dialog" role="dialog" aria-modal="true" aria-labelledby="cd-purchase-dialog-title">
            <button
              className="cd-purchase-dialog-close"
              type="button"
              onClick={() => setPurchaseDialogOpen(false)}
              disabled={isPurchasing}
              aria-label="Close purchase dialog"
            >
              ✕
            </button>

            {purchaseStatus === "success" ? (
              <div className="cd-purchase-dialog-state">
                <span className="cd-purchase-dialog-icon success">✓</span>
                <h3 id="cd-purchase-dialog-title">Payment Successful</h3>
                <p>PhonePe confirmed your payment and your course order is ready.</p>
                <button
                  className="cd-purchase-dialog-primary"
                  type="button"
                  onClick={() => setPurchaseDialogOpen(false)}
                >
                  Continue
                </button>
              </div>
            ) : purchaseStatus === "processing" ? (
              <div className="cd-purchase-dialog-state">
                <span className="cd-purchase-dialog-icon">₹</span>
                <h3 id="cd-purchase-dialog-title">Opening Secure Checkout</h3>
                <p>Connecting to PhonePe for {courseData.title}...</p>
              </div>
            ) : purchaseStatus === "login-required" ? (
              <div className="cd-purchase-dialog-state">
                <span className="cd-purchase-dialog-icon">!</span>
                <h3 id="cd-purchase-dialog-title">Login Required</h3>
                <p>Please login before purchasing this course.</p>
                <button
                  className="cd-purchase-dialog-primary"
                  type="button"
                  onClick={() => {
                    setPurchaseDialogOpen(false);
                    window.location.hash = "#login";
                  }}
                >
                  Login to Continue
                </button>
              </div>
            ) : purchaseStatus === "error" ? (
              <div className="cd-purchase-dialog-state">
                <span className="cd-purchase-dialog-icon error">!</span>
                <h3 id="cd-purchase-dialog-title">Purchase Failed</h3>
                <p>{purchaseError}</p>
                <button className="cd-purchase-dialog-primary" type="button" onClick={handleEnrollClick}>
                  Try Again
                </button>
              </div>
            ) : (
              <div className="cd-purchase-dialog-state">
                <span className="cd-purchase-dialog-icon">₹</span>
                <h3 id="cd-purchase-dialog-title">Preparing Secure Checkout</h3>
                <p>Connecting to PhonePe for {courseData.title}...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MOBILE / TABLET FLOATING BUTTON */}
      <div className="cd-mobile-sticky-enroll-bar">
        <div className="cd-mobile-bar-price-col">
          <span className="cd-bar-offer-label">Limited Offer</span>
          <div className="cd-bar-price-row">
            <h4>₹{courseData.price.toLocaleString("en-IN")}</h4>
            <span className="cd-bar-strike-price">
              {courseData.originalPrice}
            </span>
          </div>
        </div>
        {renderEnrollmentAction("cd-mobile-floating-enroll-btn", "Join Now ➔")}
      </div>
    </div>
  );
}